import { useState } from "react";

import { cn } from "@/lib/utils";

/**
 * A partir de cuántos caracteres se ofrece desplegar el texto.
 *
 * MEDIDO, NO ELEGIDO (08-09-2026, sobre el catálogo real): de los 4.450 avisos
 * con descripción, 88 —el 2 %— miden 140 caracteres o menos, que es lo que
 * entra en las dos líneas del recorte al ancho de la tarjeta. El 98 % restante
 * promedia 917 y llega hasta 5.395.
 *
 * SE DECIDE POR EL LARGO DEL TEXTO Y NO MIDIENDO LA CAJA, que sería lo exacto.
 * Medir pide `scrollHeight` contra `clientHeight` después de pintar, y eso solo
 * existe en un navegador de verdad: en la suite —que corre sin motor de
 * maquetado— las dos alturas son cero, así que el desplegable nunca aparecería
 * y ningún test podría custodiar que funciona. Un umbral es aproximado y
 * verificable; una medición exacta que nadie puede probar, no.
 */
const LARGO_QUE_YA_CABE = 140;

interface DescripcionDelAvisoProps {
  /** La prosa congelada del aviso. `null` en toda transacción del CBR. */
  descripcion: string | null;
  /**
   * Dónde se para el bloque dentro de su tarjeta.
   *
   * NO TRAE PADDING PROPIO, y es a propósito: las dos tarjetas que lo usan lo
   * resuelven distinto —una es de secciones a sangre separadas por líneas, la
   * otra tiene su padding en el `article`—, así que un relleno acá se sumaría
   * al de una de las dos.
   */
  className?: string;
}

/**
 * La prosa con que el aviso se publicó, recortada a dos líneas.
 *
 * Es lo único de una ficha que no es una medición, y dice lo que ninguna
 * columna alcanza: el riego, los deslindes, qué hay plantado, si la casa está
 * habitable. Sin ella, dos predios de la misma superficie y el mismo precio se
 * ven idénticos.
 *
 * VA RECORTADA, Y NO ES UNA PREFERENCIA DE DISEÑO. El listado del paso 2 trae
 * seis fichas por página; con 917 caracteres de promedio, la prosa completa
 * multiplica por cuatro el largo de la página y el listado deja de poder
 * recorrerse, que es exactamente para lo que existe.
 *
 * EL TEXTO ENTERO ESTÁ SIEMPRE EN EL DOCUMENTO. Lo que lo acorta es el recorte
 * visual (`line-clamp`), no una versión abreviada del contenido: así el buscador
 * del navegador —Ctrl+F, que es como se busca una palabra en una lista larga— y
 * un lector de pantalla lo encuentran aunque esté plegado.
 *
 * DETIENE EL CLIC SIEMPRE, y esto no es defensivo de más. En el catálogo TODA
 * la ficha alterna la selección del comparable, así que sin detenerlo leer una
 * descripción seleccionaría el aviso y volver a plegarla lo desmarcaría. En la
 * pestaña del informe no hay nada que alternar y detenerlo no cuesta nada, así
 * que la regla vive acá adentro en vez de repetirse —y olvidarse— en cada uso.
 */
export function DescripcionDelAviso({ descripcion, className }: DescripcionDelAvisoProps) {
  const [desplegada, setDesplegada] = useState(false);

  const texto = descripcion?.trim() ?? "";

  // Un aviso sin prosa no tiene nada que mostrar, y el bloque se OMITE en vez
  // de dibujarse vacío o con un "Sin descripción". Ese texto sería el sistema
  // hablando en el lugar del vendedor, y en una transacción del CBR —que no
  // tiene aviso— además diría que falta algo que nunca existió.
  if (texto === "") {
    return null;
  }

  const seRecorta = texto.length > LARGO_QUE_YA_CABE;

  return (
    <div className={className}>
      <p
        className={cn(
          "text-ink-body text-[0.78125rem] leading-[1.55] whitespace-pre-line",
          seRecorta && !desplegada && "line-clamp-2",
        )}
      >
        {texto}
      </p>

      {seRecorta && (
        <button
          type="button"
          onClick={(evento) => {
            evento.stopPropagation();
            setDesplegada((estaba) => !estaba);
          }}
          // A mano y no con el `Button` del DS, igual que la casilla y los dos
          // botones de `FichaDeComparable` y por el mismo motivo: el tamaño más
          // chico del DS mide 36 px de alto y acá es un enlace dentro de un
          // párrafo, no una acción de la tarjeta.
          className="text-primary mt-[0.25rem] text-[0.6875rem] font-bold hover:underline"
        >
          {desplegada ? "Ver menos" : "Ver descripción completa"}
        </button>
      )}
    </div>
  );
}
